import { useEffect, useState } from "react";
import type { AttendeePresenceState } from "@verity-connect/contracts";
import { Link } from "./router";

/**
 * The small button in the corner of a seminar's title box: scan the QR code to check in.
 *
 * Checking in is how a teacher's attendance reaches her continuing-education record, and
 * until this existed nothing in the app said so. The instruction lived on the printed page
 * in the room and on the credit page, if she happened to open it. Nathan's rule for every
 * place it appears is one sentence — "Scan the QR code to check in" — and this button is
 * that sentence made tappable.
 *
 * It does not open the camera. The installed application has no camera permission, and
 * iOS gives a page no way to launch the phone's own camera; the phone's camera reads the
 * printed square and lands on `/present` by itself. What the button does is take her to
 * the same screen with the code box, for the moment the square will not read.
 *
 * Once her mark is on file, the button becomes the receipt: green, with the time. It stays
 * that way for the rest of the session, which is how she knows she need not scan again.
 */
export function CheckInButton({ eventSlug, itemId, marks: given }: {
  eventSlug: string;
  itemId: string;
  /** For tests, and for a page that already holds the answer. Fetched when absent. */
  marks?: AttendeePresenceState["marks"];
}) {
  const [fetched, setFetched] = useState<AttendeePresenceState["marks"] | null>(null);
  const marks = given ?? fetched;

  useEffect(() => {
    if (given) return;
    const controller = new AbortController();
    const load = () => {
      void fetch(`/api/attendee/events/${encodeURIComponent(eventSlug)}/presence`, {
        credentials: "same-origin", signal: controller.signal,
      })
        .then(async (response) => (response.ok ? response.json() as Promise<AttendeePresenceState> : null))
        .then((state) => { if (state && !controller.signal.aborted) setFetched(state.marks); })
        .catch(() => { /* Signed out, or no signal. The button simply offers to check in. */ });
    };
    load();
    // She comes back from the code screen, or from the camera, with a fresh mark. Asking
    // again when the page is looked at is how the corner turns green without a reload.
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      controller.abort();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [eventSlug, itemId, given]);

  const mark = checkedIn(marks, itemId);
  if (mark) {
    return (
      <span className="check-in is-done" aria-label={`Checked in at ${clockOf(mark.markedAt)}`}>
        <CheckIcon />
        <span>{clockOf(mark.markedAt)}</span>
      </span>
    );
  }
  return (
    <Link className="check-in" href={`/present?from=${encodeURIComponent(`/schedule/${itemId}`)}`} aria-label="Scan the QR code to check in">
      <ScanIcon />
      <span>Check in</span>
    </Link>
  );
}

/** The mark for this session, if she has one. Exported so the rule can be tested alone. */
export function checkedIn(
  marks: AttendeePresenceState["marks"] | null | undefined,
  itemId: string,
): { itemId: string; markedAt: string } | null {
  return marks?.find((mark) => mark.itemId === itemId) ?? null;
}

function clockOf(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function ScanIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
      <path d="M8 8h3v3H8zM13 8h3v3h-3zM8 13h3v3H8zM13 13h1.5M16 13v3h-3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}

/**
 * Her check-in marks for this convention, or null while unknown or signed out.
 *
 * Null and empty are different answers on purpose: a screen that nags about a gap must not
 * do so to somebody who is simply not signed in, and cannot tell the two apart from `[]`.
 */
export function usePresenceMarks(eventSlug: string): AttendeePresenceState["marks"] | null {
  const [marks, setMarks] = useState<AttendeePresenceState["marks"] | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void fetch(`/api/attendee/events/${encodeURIComponent(eventSlug)}/presence`, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => (response.ok ? response.json() as Promise<AttendeePresenceState> : null))
      .then((state) => { if (state && !controller.signal.aborted) setMarks(state.marks); })
      .catch(() => { /* Signed out or no signal: unknown, and said so with null. */ });
    return () => controller.abort();
  }, [eventSlug]);
  return marks;
}

